import React, { useState, useMemo, useEffect } from "react";
import MainHeader from "../../shared/MainHeader";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CaseSearchDropdown from "../../shared/CaseSearchDropdown";
import { toast } from "react-toastify";
import { GET, POST } from "../../api/ApiService";
import dayjs from "dayjs";
import moment from "moment";
import EncryptionHelper from "../../shared/EncryptionHelper";
import { useTheme, TextField } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import axios from "axios";

const BillingPage = () => {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [uninvoicedDARs, setUninvoicedDARs] = useState([]);
  const [selectedDARs, setSelectedDARs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Activity");
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [hourlyRate, setHourlyRate] = useState("");
  const [payers, setPayers] = useState([{ name: "", percentage: 100 }]);
  const [payments, setPayments] = useState([]);
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [selectedPayer, setSelectedPayer] = useState("");
  const [invoicePayers, setInvoicePayers] = useState([]);

  const formatInvoiceStatus = (status) => {
    switch (status) {
      case "paid":
        return "Paid";
      case "partially_paid":
        return "Partially Paid";
      case "issued":
        return "Issued";
      default:
        return status;
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const themedInputSx = {
    mt: 1,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor:
          theme.palette.mode === "dark"
            ? theme.palette.grey[500]
            : theme.palette.grey[400],
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  /* ===============================
     CASE SEARCH
  =============================== */
  const formik = useFormik({
    initialValues: { case: null },
    validationSchema: Yup.object({
      case: Yup.object().required("Case is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setShowTable(false);
      const CaseID = values.case.CaseID;

      try {
        const [darResp, invoiceResp, paymentResp] = await Promise.all([
          GET(`daily-activity/getdarbycase?caseId=${CaseID}`),
          GET(`invoices/getByCase?caseId=${CaseID}`),
          GET(`payments/getByCase?caseId=${CaseID}`),
        ]);

        setPayments(paymentResp?.data || []);
        setActivities(darResp?.data || []);
        setInvoices(invoiceResp?.data || []);
        setSelectedTab("Activity");
        setShowTable(true);
      } catch (err) {
        toast.error("Failed to load billing data");
      } finally {
        setLoading(false);
      }
    },
  });

  const totalPayments = useMemo(() => {
    return payments.reduce((sum, p) => sum + Number(p.amount), 0);
  }, [payments]);

  /* ===============================
     TOTAL DURATION
  =============================== */
  const totalDuration = useMemo(() => {
    let totalMinutes = 0;
    activities.forEach((item) => {
      if (item.TimeSpent) {
        const [h, m] = item.TimeSpent.split(":").map(Number);
        totalMinutes += h * 60 + m;
      }
    });

    return {
      formatted: `${String(Math.floor(totalMinutes / 60)).padStart(
        2,
        "0",
      )}:${String(totalMinutes % 60).padStart(2, "0")}`,
      decimalHours: (totalMinutes / 60).toFixed(2),
    };
  }, [activities]);

  /* ===============================
     INVOICE MODAL
  =============================== */
  const openInvoiceModal = async () => {
    try {
      if (!formik.values.case?.CaseID) {
        toast.error("Please select case");
        return;
      }

      const resp = await GET(
        `daily-activity/get-not-invoiced?caseId=${formik.values.case?.CaseID}`,
      );
      const decrytedData = EncryptionHelper.decryptData(resp?.encryptedData);
      console.log("Uninvoiced DARs:", decrytedData);
      setUninvoicedDARs(decrytedData || []);
      setSelectedDARs([]);
      setInvoiceModal(true);
      setPayers([{ name: "", percentage: 100 }]);
      setHourlyRate("");
    } catch (err) {
      toast.error(
        err?.response?.data?.error?.message || "Failed to load activities",
      );
    }
  };

  const toggleDAR = (id) => {
    setSelectedDARs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handlePayerChange = (index, field, value) => {
    if (field === "percentage" && Number(value) > 100) return;

    const updated = [...payers];
    updated[index][field] = value;
    setPayers(updated);
  };

  const createPayment = async () => {
    try {
      const payload = {
        caseId: formik.values.case.CaseID,
        invoiceId: selectedInvoice,
        payerName: selectedPayer,
        amount: paymentAmount,
      };

      if (!selectedInvoice) {
        toast.error("Please select invoice");
        return;
      }

      if (!selectedPayer) {
        toast.error("Please select payer");
        return;
      }

      if (!paymentAmount || Number(paymentAmount) <= 0) {
        toast.error("Enter valid amount");
        return;
      }

      const selectedInv = invoices.find((i) => i?.id == selectedInvoice);

      if (Number(paymentAmount) > selectedInv?.remainingBalance) {
        const confirm = window.confirm(
          "The payment amount is greater than the Invoice balance. Are you sure you want to proceed?",
        );

        if (!confirm) return;
      }

      const encryptData = EncryptionHelper.encryptData(payload);

      await POST("payments/create", { encryptData });

      toast.success("Payment added successfully");

      setPaymentModal(false);
      setSelectedInvoice("");
      setSelectedPayer("");
      setPaymentAmount("");
      setInvoicePayers([]);

      const paymentResp = await GET(
        `payments/getByCase?caseId=${formik.values.case.CaseID}`,
      );

      setPayments(paymentResp.data || []);

      // ✅ Refresh Invoices
      await fetchInvoicesByCase(formik.values.case.CaseID);

      setSelectedInvoice("");
    } catch (err) {
      toast.error("Failed to create payment");
    }
  };

  const addPayer = () => {
    if (payers.length >= 4) {
      toast.warning("Maximum 4 payers allowed");
      return;
    }

    setPayers([...payers, { name: "", percentage: 0 }]);
  };

  const totalPercentage = useMemo(() => {
    return payers.reduce((sum, p) => sum + Number(p.percentage || 0), 0);
  }, [payers]);

  const selectedSummary = useMemo(() => {
    let totalMinutes = 0;

    uninvoicedDARs.forEach((dar) => {
      if (selectedDARs.includes(dar.ReportID) && dar.TimeSpent) {
        const [h, m] = dar.TimeSpent.split(":").map(Number);
        totalMinutes += h * 60 + m;
      }
    });

    const decimalHours = totalMinutes / 60;
    const totalAmount =
      hourlyRate && decimalHours
        ? (decimalHours * Number(hourlyRate)).toFixed(2)
        : 0;

    return {
      totalMinutes,
      decimalHours: decimalHours.toFixed(2),
      totalAmount,
    };
  }, [selectedDARs, uninvoicedDARs, hourlyRate]);

  const selectedInvoiceBalance = useMemo(() => {
    const invoice = invoices.find((i) => i?.id == selectedInvoice);
    return invoice?.remainingBalance ?? null;
  }, [invoices, selectedInvoice]);

  useEffect(() => {
    const fetchInvoicePayers = async () => {
      if (!selectedInvoice) return;

      try {
        const resp = await GET(
          `invoices/getPayers?invoiceId=${selectedInvoice}`,
        );
        setInvoicePayers(resp?.data || []);
      } catch (err) {
        toast.error("Failed to load payers");
      }
    };

    fetchInvoicePayers();
  }, [selectedInvoice]);

  const createInvoice = async () => {
    if (!selectedDARs.length) {
      toast.warning("Select at least one activity");
      return;
    }

    if (!hourlyRate || Number(hourlyRate) <= 0) {
      toast.warning("Enter valid hourly rate");
      return;
    }

    if (totalPercentage !== 100) {
      toast.error("Total payer responsibility must equal 100%");
      return;
    }

    for (let payer of payers) {
      if (!payer.name) {
        toast.error("All payer names are required");
        return;
      }
    }

    try {
      const payload = {
        CaseID: formik.values.case.CaseID,
        CaseName: formik.values.case?.CaseName,
        DocketIndexNumber: formik.values.case?.DocketIndexNumber,
        DarItemIds: selectedDARs,
        HourlyRate: hourlyRate,
        // TotalHours: selectedSummary.decimalHours,
        // TotalAmount: selectedSummary.totalAmount,
        Payers: payers.map((payer) => ({
          ...payer,
          balanceDue: (selectedSummary.totalAmount * payer.percentage) / 100,
        })),
      };

      const encryptData = EncryptionHelper.encryptData(payload);

      await POST("invoices/create", { encryptData });

      toast.success("Invoice created successfully");
      setInvoiceModal(false);
      // ✅ Refresh invoices
      await fetchInvoicesByCase(formik.values.case.CaseID);

      // ✅ Switch to Invoice tab automatically
      setSelectedTab("Invoices");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error creating invoice");
    }
  };

  const fetchInvoicesByCase = async (caseId) => {
    try {
      const invoiceResp = await GET(`invoices/getByCase?caseId=${caseId}`);
      setInvoices(invoiceResp?.data || []);
    } catch (err) {
      toast.error("Failed to refresh invoices");
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/invoices/download/${invoiceId}`,
        {
          responseType: "blob",
          headers: {
            Authorization: token,
            "X-API-Key": process.env.REACT_APP_API_KEY,
          },
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${invoiceId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Failed to download invoices");
    }
  };

  return (
    <Box p={3}>
      <MainHeader headingText="Billing" />

      {/* ===== Case Search ===== */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography>Case *</Typography>
              <CaseSearchDropdown
                value={formik.values.case}
                onChange={(e, v) => formik.setFieldValue("case", v)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={22} /> : "Search"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Collapse in={showTable}>
        <>
          <Paper sx={{ mt: 3 }}>
            <Tabs value={selectedTab} onChange={handleTabChange}>
              <Tab value="Activity" label="Activity" />
              <Tab value="Invoices" label="Invoices" />
              <Tab value="Payments" label="Payments" />
            </Tabs>
          </Paper>

          {/* ===== ACTIVITY TAB ===== */}
          {selectedTab === "Activity" && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6">Daily Activity Reports</Typography>

              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Created At</TableCell>
                      <TableCell>Service or Activity</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Case Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((row) => (
                      <TableRow key={row.ReportID}>
                        <TableCell>
                          {row.CreatedAt
                            ? moment(row.CreatedAt).format("LLL")
                            : "null"}
                        </TableCell>
                        <TableCell>{row?.ServiceOrActivity}</TableCell>
                        <TableCell>
                          {row?.StartTime
                            ? moment(row?.StartTime).format("LLL")
                            : "null"}
                        </TableCell>
                        <TableCell>
                          {row?.FinishTime
                            ? moment(row?.FinishTime).format("LLL")
                            : "null"}
                        </TableCell>
                        <TableCell>{row.TimeSpent}</TableCell>
                        <TableCell>{row.Username}</TableCell>
                        <TableCell>{row?.CaseName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />
              <Typography fontWeight="bold">
                Total Time: {totalDuration.formatted} hrs (
                {totalDuration.decimalHours})
              </Typography>
            </Paper>
          )}

          {/* ===== INVOICES TAB ===== */}
          {selectedTab === "Invoices" && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Invoices</Typography>
                <Button variant="contained" onClick={openInvoiceModal}>
                  Create Invoice
                </Button>
              </Stack>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Balance Due</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.invoiceNumber}</TableCell>
                      <TableCell>{formatInvoiceStatus(inv.status)}</TableCell>
                      <TableCell>
                        {inv.totalAmount !== null ? `$${inv.totalAmount}` : "-"}
                      </TableCell>

                      <TableCell>
                        {inv.remainingBalance !== null
                          ? `$${inv.remainingBalance}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {dayjs(inv.createdAt).format("DD-MMM-YYYY")}
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => downloadInvoice(inv.id)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          {selectedTab === "Payments" && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Payments</Typography>

                <Button
                  variant="contained"
                  onClick={() => setPaymentModal(true)}
                >
                  + Add Payment
                </Button>
              </Stack>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Payer Name</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.payer_name}</TableCell>
                      <TableCell>{p.invoice_number}</TableCell>
                      <TableCell>${p.amount}</TableCell>
                      <TableCell>
                        {dayjs(p.created_at).format("DD-MMM-YYYY")}
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      <b>Total</b>
                    </TableCell>

                    <TableCell colSpan={2}>
                      <b>${totalPayments.toFixed(2)}</b>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      </Collapse>

      {/* ===== CREATE INVOICE MODAL ===== */}
      <Dialog open={invoiceModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          Create Invoice for {formik.values.case?.CaseName}
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Created At</TableCell>
                  <TableCell>Service or Activity</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {Array.isArray(uninvoicedDARs) && uninvoicedDARs.length > 0 ? (
                  uninvoicedDARs.map((dar) => (
                    <TableRow key={dar.ReportID} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDARs.includes(dar.ReportID)}
                          onChange={() => toggleDAR(dar.ReportID)}
                        />
                      </TableCell>

                      <TableCell>
                        {dar.CreatedAt
                          ? moment(dar.CreatedAt).format("LLL")
                          : "-"}
                      </TableCell>

                      <TableCell>{dar.ServiceOrActivity}</TableCell>

                      <TableCell>
                        {dar.StartTime
                          ? moment(dar.StartTime).format("LLL")
                          : "-"}
                      </TableCell>

                      <TableCell>
                        {dar.FinishTime
                          ? moment(dar.FinishTime).format("LLL")
                          : "-"}
                      </TableCell>

                      <TableCell>{dar.TimeSpent}</TableCell>

                      <TableCell>{dar.Username || "-"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" m={2}>
                      No uninvoiced activities found for this case
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={3}>
            <Typography fontWeight="bold">Hourly Rate *</Typography>
            <TextField
              // fullWidth
              type="number"
              size="small"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              sx={themedInputSx}
            />
          </Box>
          <Box mt={4}>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography fontWeight="bold">Split Payment</Typography>

              <Button variant="outlined" size="small" onClick={addPayer}>
                Split Payment
              </Button>
            </Stack>

            {payers.map((payer, index) => (
              <Grid container spacing={2} key={index} mb={2}>
                <Grid item xs={6}>
                  <Typography>Payer Name *</Typography>

                  <TextField
                    fullWidth
                    size="small"
                    value={payer.name}
                    onChange={(e) =>
                      handlePayerChange(index, "name", e.target.value)
                    }
                    sx={themedInputSx}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography>Responsibility (%) *</Typography>

                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    value={payer.percentage}
                    onChange={(e) =>
                      handlePayerChange(index, "percentage", e.target.value)
                    }
                    sx={themedInputSx}
                  />
                </Grid>
              </Grid>
            ))}

            {/* <Box mt={4} p={2} border="1px solid" borderColor="divider">
              <Typography fontWeight="bold" mb={1}>
                Invoice Preview
              </Typography>

              <Typography>
                Total Hours: {selectedSummary.decimalHours}
              </Typography>

              <Typography>Hourly Rate: ${hourlyRate || 0}</Typography>

              <Typography fontWeight="bold">
                Total Invoice Amount: ${selectedSummary.totalAmount}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {payers.map((payer, index) => {
                const balance =
                  selectedSummary.totalAmount && payer.percentage
                    ? (
                        (selectedSummary.totalAmount * payer.percentage) /
                        100
                      ).toFixed(2)
                    : 0;

                return (
                  <Typography key={index}>
                    {payer.name || "Payer"} → ${balance}
                  </Typography>
                );
              })}
            </Box> */}

            <Typography
              mt={1}
              fontWeight="bold"
              color={
                totalPercentage !== 100
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
            >
              Total Responsibility: {totalPercentage}%
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setInvoiceModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={createInvoice}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Payment</DialogTitle>

        <DialogContent>
          {/* Invoice Dropdown */}
          <Typography>Select Invoice</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedInvoice}
            onChange={(e) => setSelectedInvoice(e.target.value)}
            sx={themedInputSx}
            SelectProps={{ native: true }}
          >
            <option value="">Select Invoice</option>

            {invoices
              .filter((i) => i?.remainingBalance > 0)
              .map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.invoiceNumber}
                </option>
              ))}
          </TextField>

          {selectedInvoiceBalance !== null && (
            <Typography mt={1} color="textSecondary">
              Remaining Balance: ${selectedInvoiceBalance}
            </Typography>
          )}

          {/* Payer Dropdown */}
          <Typography mt={2}>Payer</Typography>

          <TextField
            select
            fullWidth
            size="small"
            value={selectedPayer}
            onChange={(e) => setSelectedPayer(e.target.value)}
            sx={themedInputSx}
            SelectProps={{ native: true }}
          >
            <option value="">Select Payer</option>
            {invoicePayers.map((payer, i) => (
              <option key={i} value={payer.name}>
                {payer.name}
              </option>
            ))}
          </TextField>

          {/* Amount */}
          <Typography mt={2}>Amount</Typography>

          <TextField
            type="number"
            fullWidth
            size="small"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            sx={themedInputSx}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPaymentModal(false)}>Cancel</Button>

          <Button variant="contained" onClick={createPayment}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPage;
